import { RefObject, useCallback, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PagedList, usePagedList } from "../../../utils/usePagedList";

export const DIRECTORY_PAGE_SIZE = 20;

export interface DirectoryItem {
  tags?: string[] | null;
}

interface DirectoryListOptions<T> {
  items: T[];
  // The tags offered as filters, in display order.
  availableTags: string[];
  // Both must keep their identity between renders (module scope or memoised).
  categoryOf: (item: T) => string;
  // `query` is trimmed, lower case and never empty.
  matchesSearch: (item: T, query: string) => boolean;
  compare?: (a: T, b: T) => number;
  // Anything else that changes the list, e.g. a search option. A change goes
  // back to page 1 like a search, tag, category or sort change.
  resetKeys?: ReadonlyArray<unknown>;
}

export interface DirectoryList<T> {
  search: string;
  setSearch: (search: string) => void;
  availableTags: string[];
  checkedTags: ReadonlySet<string>;
  // Items per tag in the ticked categories, whatever the search and tags.
  tagCounts: Record<string, number>;
  toggleTag: (tag: string) => void;
  // A card's tag button: that tag only, no search.
  showOnlyTag: (tag: string) => void;
  categories: string[];
  isCategoryChecked: (category: string) => boolean;
  toggleCategory: (category: string, checked: boolean) => void;
  allCategoriesChecked: boolean;
  setAllCategories: (checked: boolean) => void;
  // Ticked tags and unticked categories; the search is left alone.
  clearFilters: () => void;
  activeFilterCount: number;
  filtered: T[];
  paged: PagedList<T>;
  goToPage: (page: number) => void;
  // Attached by DirectoryLayout: the top of the list and the sticky banner.
  listTopRef: RefObject<HTMLDivElement>;
  stickyTopRef: RefObject<HTMLDivElement>;
}

const tagsOf = (item: DirectoryItem) =>
  Array.isArray(item.tags) ? item.tags : [];

// Filtering and paging of the Providers and Resources directories:
// categories -> tags (any ticked tag) -> search -> sort -> page of 20. The
// page is `?page=` in the URL (replaced, other params kept), so the full-page
// reloads after an edit come back to it.
export function useDirectoryList<T extends DirectoryItem>({
  items,
  availableTags,
  categoryOf,
  matchesSearch,
  compare,
  resetKeys = [],
}: DirectoryListOptions<T>): DirectoryList<T> {
  const [search, setSearch] = useState("");
  const [checkedTags, setCheckedTags] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // Categories start ticked, so the unticked ones are kept.
  const [excludedCategories, setExcludedCategories] = useState<
    ReadonlySet<string>
  >(() => new Set());

  const categories = useMemo(
    () => Array.from(new Set(items.map(categoryOf))).sort(),
    [items, categoryOf],
  );
  const inCategories = useMemo(
    () => items.filter((item) => !excludedCategories.has(categoryOf(item))),
    [items, excludedCategories, categoryOf],
  );
  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    inCategories.forEach((item) =>
      tagsOf(item).forEach((tag) => {
        counts[tag] = (counts[tag] ?? 0) + 1;
      }),
    );
    return counts;
  }, [inCategories]);

  const query = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    let list = inCategories;
    if (checkedTags.size > 0) {
      list = list.filter((item) =>
        tagsOf(item).some((tag) => checkedTags.has(tag)),
      );
    }
    if (query) {
      list = list.filter((item) => matchesSearch(item, query));
    }
    return compare ? [...list].sort(compare) : list;
  }, [inCategories, checkedTags, query, matchesSearch, compare]);

  const [searchParams, setSearchParams] = useSearchParams();
  const setUrlPage = useCallback(
    (page: number) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (page > 1) next.set("page", String(page));
          else next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const paged = usePagedList(
    filtered,
    DIRECTORY_PAGE_SIZE,
    [query, checkedTags, excludedCategories, compare, ...resetKeys],
    // Page 1 has no param; a missing or malformed one reads as 1.
    { page: Number(searchParams.get("page")) || 1, onPageChange: setUrlPage },
  );

  const listTopRef = useRef<HTMLDivElement>(null);
  const stickyTopRef = useRef<HTMLDivElement>(null);
  // When the page is scrolled past the top of the list, bring it back just
  // below the sticky case banner.
  const scrollToListTop = () => {
    const listTop = listTopRef.current;
    if (!listTop) return;
    const covered = stickyTopRef.current?.getBoundingClientRect().bottom ?? 0;
    const top = listTop.getBoundingClientRect().top;
    if (top < covered) window.scrollBy({ top: top - covered - 8 });
  };

  return {
    search,
    setSearch,
    availableTags,
    checkedTags,
    tagCounts,
    toggleTag: (tag) =>
      setCheckedTags((current) => {
        const next = new Set(current);
        if (!next.delete(tag)) next.add(tag);
        return next;
      }),
    showOnlyTag: (tag) => {
      setCheckedTags(new Set(availableTags.includes(tag) ? [tag] : []));
      setSearch("");
      scrollToListTop();
    },
    categories,
    isCategoryChecked: (category) => !excludedCategories.has(category),
    toggleCategory: (category, checked) =>
      setExcludedCategories((current) => {
        const next = new Set(current);
        if (checked) next.delete(category);
        else next.add(category);
        return next;
      }),
    allCategoriesChecked: categories.every(
      (category) => !excludedCategories.has(category),
    ),
    setAllCategories: (checked) =>
      setExcludedCategories(new Set(checked ? [] : categories)),
    clearFilters: () => {
      setCheckedTags(new Set());
      setExcludedCategories(new Set());
    },
    activeFilterCount:
      checkedTags.size +
      categories.filter((category) => excludedCategories.has(category)).length,
    filtered,
    paged,
    goToPage: (page) => {
      paged.setPage(page);
      scrollToListTop();
    },
    listTopRef,
    stickyTopRef,
  };
}
